import React, { useState } from 'react';
import { Box, Tabs, Tab, Typography, Card, CardContent, CardActions, Button, Grid, Alert } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FolderIcon from '@mui/icons-material/Folder';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OutstandingAmount from './OutstandingAmount';
import Ledger from './Ledger';

const vendorDocs = [
  { label: 'GST WTC NEW', file: '/GST WTC NEW.pdf' },
  { label: 'HDFC Bank Mandate', file: '/HDFC Bank mandate_.pdf' },
  { label: 'QCI PAN Card', file: '/QCI PAN CARD.pdf' },
];

const VendorDocs = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 3 }}>
      Vendor Registration Documents
    </Typography>
     <Alert
          variant="filled"
          severity="warning"
          sx={{
            mb: 2.5,
            fontWeight: 700,
            fontSize: '0.95rem',
            letterSpacing: 0.8,
            borderRadius: 2,
          }}
        >
          QCI IS NOT REGISTERED UNDER MSME
        </Alert>
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {vendorDocs.map((doc) => (
        <Card key={doc.file} elevation={2} sx={{ width: 260, borderTop: '4px solid #00024c' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pb: 1 }}>
            <PictureAsPdfIcon sx={{ fontSize: 56, color: '#d32f2f' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: '#00024c' }}>
              {doc.label}
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(process.env.PUBLIC_URL + doc.file, '_blank')}
              sx={{ bgcolor: '#00024c', '&:hover': { bgcolor: '#000380' }, textTransform: 'none' }}
            >
              View Document
            </Button>
          </CardActions>
        </Card>
      ))}
    </Box>
  </Box>
);

const VirtualAccount = ({ username }) => {
  const [copied, setCopied] = useState(false);
  const virtualAccountNumber = username ? `NABLQC${username.replace(/^(nabln|nabl)/i, '')}` : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(virtualAccountNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 3 }}>
        Bank / Virtual Account Details
      </Typography>

      {!username && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Search a lab in the Outstanding Amount tab first to see their virtual account number.
        </Alert>
      )}

      <Card elevation={2} sx={{ maxWidth: 600, borderTop: '4px solid #00024c' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            HDFC Bank — Other Payment Modes
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Beneficiary Name:</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2">Quality Council of India</Typography></Grid>

            <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Bank Name:</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2">HDFC Bank</Typography></Grid>

            <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Branch:</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2">Kanjurmarg Branch, Mumbai</Typography></Grid>

            <Grid item xs={6}><Typography variant="body2" fontWeight="bold">IFSC Code:</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2">HDFC0004989</Typography></Grid>

            <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Type of Account:</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2">Saving Account</Typography></Grid>

            <Grid item xs={6}><Typography variant="body2" fontWeight="bold">Virtual Account Number:</Typography></Grid>
            <Grid item xs={6}>
              {virtualAccountNumber ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '1rem' }}>
                    {virtualAccountNumber}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={handleCopy}
                    sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
                    variant="outlined"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">— search lab first —</Typography>
              )}
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            After making payment, Virtual Account number shall be entered at the portal using offline payment functionality given at your dashboard to get the payment updated in your account.
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

const OutstandingLedger = () => {
  const [tab, setTab] = useState(0);
  const [searchedUsername, setSearchedUsername] = useState('');

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
            '& .Mui-selected': { color: '#00024c' },
            '& .MuiTabs-indicator': { backgroundColor: '#00024c' },
          }}
        >
          <Tab icon={<AccountBalanceWalletIcon />} iconPosition="start" label="Outstanding Amount" />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Ledger" />
          <Tab icon={<FolderIcon />} iconPosition="start" label="Vendor Registration Document" />
          <Tab icon={<AccountBalanceIcon />} iconPosition="start" label="Virtual Account" />
        </Tabs>
      </Box>
      {tab === 0 && <OutstandingAmount onSearch={setSearchedUsername} />}
      {tab === 1 && <Ledger />}
      {tab === 2 && <VendorDocs />}
      {tab === 3 && <VirtualAccount username={searchedUsername} />}
    </Box>
  );
};

export default OutstandingLedger;